import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AccountActivationResponse,
  VerifyOtpPayload,
  VerifyOtpResponse,
  SetPasswordPayload,
  SetPasswordResponse,
  LoginCredentialsPayload,
  VerifyGoogleAuthPayload,
  GoogleAuthLoginSetupResponse,
  LoginTokensApiResponse,
  CompletePasswordRecoveryResponse,
  SubmitCredentialResponse,
} from '../models/auth.model';
import { ApiResponse } from '../../shared/models/api-response.models';
import {
  withSuppressErrorToast,
  withSuppressSuccessToast,
} from '../../core/http/api-response.envelope';
import { DeviceInfoService } from '@core/services/device-info.service';

/**
 * All POSTs in this service return the standard server envelope
 * `ApiResponse<T>` (see src/app/shared/models/api-response.models.ts).
 * Callers must read inner fields through `res.data?.…`.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.userManagementApiUrl;
  private readonly deviceInfo = inject(DeviceInfoService);

  constructor(private http: HttpClient) {}

  checkUsername(username: string): Observable<AccountActivationResponse> {
    return this.http.post<AccountActivationResponse>(
      `${this.apiUrl}/api/auth/v1/signup`,
      { phone: username }
    );
  }

  verifyOtp(payload: VerifyOtpPayload): Observable<VerifyOtpResponse> {
    const headers = this.buildDeviceHeaders().set(
      'Authorization',
      'Bearer ' + payload.activationToken
    );
    return this.http.post<VerifyOtpResponse>(
      `${this.apiUrl}/api/auth/v1/otp/verify`,
      { otpCode: payload.otpCode.toString() },
      { headers }
    );
  }

  setPassword(payload: SetPasswordPayload): Observable<SetPasswordResponse> {
    const headers = this.buildDeviceHeaders().set(
      'Authorization',
      'Bearer ' + payload.activationToken
    );
    return this.http.post<SetPasswordResponse>(
      `${this.apiUrl}/api/auth/v1/password/setup`,
      { password: payload.password },
      { headers }
    );
  }

  /**
   * The password step. It identifies the device too: a refused password is
   * recorded in the owner's login history with the device it came from.
   */
  login(
    payload: LoginCredentialsPayload
  ): Observable<SubmitCredentialResponse> {
    return this.http.post<SubmitCredentialResponse>(
      `${this.apiUrl}/api/auth/v1/login`,
      { phone: payload.phone, password: payload.password },
      { headers: this.buildDeviceHeaders() }
    );
  }

  /**
   * The last sign-in step when the login rule requires Google Authenticator.
   * Answers like `otp/verify` on completion: `token` + `refreshToken`.
   */
  verifyGoogleAuth(
    payload: VerifyGoogleAuthPayload
  ): Observable<VerifyOtpResponse> {
    // The device headers, as verifyOtp and setPassword send them: this call
    // creates the session too (F-029)
    const headers = this.buildDeviceHeaders().set(
      'Authorization',
      'Bearer ' + payload.sessionToken
    );
    return this.http.post<VerifyOtpResponse>(
      `${this.apiUrl}/api/auth/v1/g-auth/login/verify`,
      { otp: payload.googleAuthCode },
      { headers: headers }
    );
  }

  /** Start Google Authenticator enrolment for a member who has none, mid sign-in. */
  startLoginGoogleAuthSetup(
    sessionToken: string
  ): Observable<GoogleAuthLoginSetupResponse> {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + sessionToken,
    });
    return this.http.post<GoogleAuthLoginSetupResponse>(
      `${this.apiUrl}/api/auth/v1/g-auth/login/setup`,
      {},
      { headers, context: withSuppressSuccessToast() }
    );
  }

  initiatePasswordRecovery(
    username: string
  ): Observable<AccountActivationResponse> {
    return this.http.post<AccountActivationResponse>(
      `${this.apiUrl}/api/auth/v1/password/forget`,
      { phone: username }
    );
  }

  completePasswordRecovery(payload: {
    activationToken: string;
    password: string;
  }): Observable<CompletePasswordRecoveryResponse> {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + payload.activationToken,
    });
    return this.http.post<CompletePasswordRecoveryResponse>(
      `${this.apiUrl}/api/auth/v1/password/forget/post-action`,
      { password: payload.password },
      { headers: headers }
    );
  }

  refreshToken(refreshToken: string): Observable<LoginTokensApiResponse> {
    // Token refresh is a silent, automatic mutation triggered by the auth
    // interceptor; it must not surface a toast of its own. A refusal reaches
    // the operator once, through the request that needed the renewal (F-018).
    return this.http.post<LoginTokensApiResponse>(
      `${this.apiUrl}/api/auth/v1/refresh`,
      { refreshToken },
      { context: withSuppressErrorToast(withSuppressSuccessToast()) }
    );
  }

  logout(): Observable<ApiResponse<void> | void> {
    return this.http.post<ApiResponse<void> | void>(
      `${this.apiUrl}/api/auth/v1/logout`,
      {}
    );
  }

  /**
   * Builds the `x-device-id` + `x-client-*` + `user-agent` headers that
   * the backend uses to fingerprint the originating device on
   * auth-initiating requests (signup / login).
   *
   * The id is created on first visit to `/auth/*` by
   * `AuthLayoutComponent`; this method just reads + falls back to
   * generating one if a caller somehow bypasses the layout (e.g. tests).
   */
  private buildDeviceHeaders(): HttpHeaders {
    return new HttpHeaders(this.deviceInfo.getAuthRequestHeaders());
  }
}
